import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from '@/contexts/useOnboardingContext';
import { useAuth } from '@/contexts/useAuth';
import { getDaysUntilInterview, updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import Button from '@/components/ui/Button';
import DatePicker from '@/components/ui/DatePicker';
import ErrorAlert from '@/components/ui/ErrorAlert';
import OnboardingShell from '@/components/layout/OnboardingShell';

export default function InterviewDatePage() {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const { state, setInterviewDate, setStep } = useOnboardingContext();
  const [mode, setMode] = useState<'date' | 'none'>(
    state.interview_date ? 'date' : 'none'
  );
  const [date, setDate] = useState(state.interview_date || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const daysUntil = date ? getDaysUntilInterview(date) : null;

  const handleContinue = async () => {
    if (!user?.id) return;
    const value = mode === 'date' && date ? date : null;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, { interview_date: value });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    setInterviewDate(value);
    setStep(3);
    await refreshProfile();
    track('onboarding_step_completed', { step: 'interview_date' });
    navigate('/onboarding/english-level');
  };

  const selectDate = () => setMode('date');
  const selectNone = () => {
    setMode('none');
    setDate('');
  };

  const cardClass = (active: boolean) =>
    `w-full text-left p-4 rounded-xl border-2 transition-colors cursor-pointer ${
      active
        ? 'border-primary bg-primary-light'
        : 'border-border bg-card hover:border-primary/30'
    }`;

  const onCardKey =
    (handler: () => void) =>
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handler();
      }
    };

  return (
    <OnboardingShell
      step={2}
      title={<>When&apos;s your interview?</>}
      subtitle={<>This helps us personalize your study plan.</>}
    >
      <div role="radiogroup" aria-label="Interview date" className="space-y-4">
        <div
          role="radio"
          aria-checked={mode === 'date'}
          tabIndex={0}
          onClick={selectDate}
          onKeyDown={onCardKey(selectDate)}
          className={cardClass(mode === 'date')}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                mode === 'date' ? 'border-primary' : 'border-border'
              }`}
            >
              {mode === 'date' && (
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
              )}
            </div>
            <span className="font-sans font-medium text-text-primary">
              Select a date
            </span>
          </div>
          {mode === 'date' && (
            <div
              className="mt-3 ml-0 sm:ml-8"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <DatePicker
                value={date || null}
                onChange={setDate}
              />
              {daysUntil !== null && daysUntil > 0 && (
                <p className="mt-3 font-sans text-sm text-success font-medium">
                  {daysUntil} {daysUntil === 1 ? 'day' : 'days'} away
                </p>
              )}
            </div>
          )}
        </div>

        <div
          role="radio"
          aria-checked={mode === 'none'}
          tabIndex={0}
          onClick={selectNone}
          onKeyDown={onCardKey(selectNone)}
          className={cardClass(mode === 'none')}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                mode === 'none' ? 'border-primary' : 'border-border'
              }`}
            >
              {mode === 'none' && (
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
              )}
            </div>
            <span className="font-sans font-medium text-text-primary">
              Not scheduled yet
            </span>
          </div>
        </div>
      </div>

      {error && <ErrorAlert error={error} className="mt-6" />}

      <div className="mt-8">
        <Button
          fullWidth
          onClick={handleContinue}
          disabled={(mode === 'date' && !date) || saving}
        >
          {saving ? 'Saving…' : 'Continue'}
        </Button>
      </div>
    </OnboardingShell>
  );
}
